import { ApprovalConfigurationStage, ApprovalConfigurationStageApprover, APPROVER_TYPE, REQUIRED_APPROVERS, StageComparison, UpdatedStage } from '../../types';

export const addApproversToStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  approvers: ApprovalConfigurationStageApprover[],
): ApprovalConfigurationStage[] =>
  stages.map(stage =>
    stage.stageNumber === stageNumber
      ? {
        ...stage,
        assignedApprovers: [
          ...stage.assignedApprovers,
          ...approvers,
        ],
      }
      : stage,
  );

export const removeApproverFromStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  approverIndex: number,
): ApprovalConfigurationStage[] =>
  stages.map(stage =>
    stage.stageNumber === stageNumber
      ? {
        ...stage,
        assignedApprovers: stage.assignedApprovers.filter(
          (approver, index) => index !== approverIndex,
        ),
      }
      : stage,
  );

export const updateApproverInStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  approverId: string,
  updates: Partial<ApprovalConfigurationStageApprover>,
): ApprovalConfigurationStage[] =>
  stages.map(stage =>
    stage.stageNumber === stageNumber
      ? {
        ...stage,
        assignedApprovers: stage.assignedApprovers.map(approver =>
          approver.id === approverId
            ? { ...approver, ...updates }
            : approver,
        ),
      }
      : stage,
  );

export const updateRequiredCountForStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  requiredCount: REQUIRED_APPROVERS,
): ApprovalConfigurationStage[] =>
  stages.map(stage =>
    stage.stageNumber === stageNumber
      ? {
        ...stage,
        requiredCount,
      }
      : stage,
  );

export const updateNameForStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  name: string,
): ApprovalConfigurationStage[] =>
  stages.map(stage =>
    stage.stageNumber === stageNumber
      ? {
        ...stage,
        name,
      }
      : stage,
  );

export const updateOptionalApproverRejectionChangeForStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  optionalApproverRejectionRejectsStage: boolean,
): ApprovalConfigurationStage[] =>
  stages.map(stage =>
    stage.stageNumber === stageNumber
      ? {
        ...stage,
        optionalApproverRejectionRejectsStage,
      }
      : stage,
  );

export const removeStage = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
): ApprovalConfigurationStage[] =>
  stages.filter(
    stage => stage.stageNumber !== stageNumber,
  ).map((stage, index) => ({
    ...stage,
    stageNumber: index + 1,
  }));

export const addApproverToStageIfMissing = (
  stages: ApprovalConfigurationStage[],
  stageNumber: number,
  approver: ApprovalConfigurationStageApprover,
): ApprovalConfigurationStage[] =>
  stages.map(stage => {
    if (stage.stageNumber !== stageNumber) {
      return stage;
    }

    const alreadyExists = stage.assignedApprovers.some(
      existing => existing.id === approver.id,
    );

    if (alreadyExists) {
      return stage;
    }

    return {
      ...stage,
      assignedApprovers: [
        ...stage.assignedApprovers,
        approver,
      ],
    };
  });

export interface StageValidationError {
  stageNumber: number
  message: string
}

export const validateStages = (
  stages: ApprovalConfigurationStage[],
): StageValidationError[] => {
  const errors: StageValidationError[] = [];

  stages.forEach(stage => {
    const requiredApprovers = stage.assignedApprovers.filter(
      approver => !approver.isOptional,
    );

    let requiredApproversCount = 0;
    requiredApprovers.forEach(
      requiredApprover => {
        if (requiredApprover.type === APPROVER_TYPE.GROUP) {
          requiredApproversCount += requiredApprover.employeeCount ?? 0;
        } else {
          requiredApproversCount += 1;
        }
      },
    );

    if (requiredApproversCount === 0) {
      errors.push({
        stageNumber: stage.stageNumber,
        message: 'Must contain at least one required approver.',
      });

      return;
    }

    switch (stage.requiredCount) {
      case REQUIRED_APPROVERS.ANY_ONE:
        // Already satisfied by the check above.
        break;

      case REQUIRED_APPROVERS.ANY_TWO:
        if (requiredApproversCount < 2) {
          errors.push({
            stageNumber: stage.stageNumber,
            message: 'Any Two requires at least two required approvers.',
          });
        }
        break;
    }
  });

  return errors;
};

export const compareStageDetails = (currentStages: ApprovalConfigurationStage[], oldStages: ApprovalConfigurationStage[]): StageComparison => {
  const currentMap = new Map(
    currentStages.map(stage => [stage.stageNumber, stage]),
  );

  const oldMap = new Map(
    oldStages.map(stage => [stage.stageNumber, stage]),
  );

  const addedStages: ApprovalConfigurationStage[] = [];
  const deletedStages: ApprovalConfigurationStage[] = [];
  const updatedStages: UpdatedStage[] = [];

  // Deleted stages
  oldStages.forEach(oldStage => {
    if (!currentMap.has(oldStage.stageNumber)) {
      deletedStages.push(oldStage);
    }
  });

  currentStages.forEach(currentStage => {
    const oldStage = oldMap.get(currentStage.stageNumber);

    if (!oldStage) {
      addedStages.push(currentStage);
      return;
    }

    const currentApprovers = new Map(
      currentStage.assignedApprovers.map(a => [a.id, a]),
    );

    const oldApprovers = new Map(
      oldStage.assignedApprovers.map(a => [a.id, a]),
    );

    const approversAdded: ApprovalConfigurationStageApprover[] = [];
    const approversDeleted: ApprovalConfigurationStageApprover[] = [];
    const approversUpdated: UpdatedStage['approversUpdated'] = [];

    currentStage.assignedApprovers.forEach(current => {
      const previous = oldApprovers.get(current.id);

      if (!previous) {
        approversAdded.push(current);
        return;
      }

      if (current.isOptional !== previous.isOptional) {
        approversUpdated.push({
          approverId: current.id,
          approverName: current.name,
          changes: {
            isOptional: {
              old: previous.isOptional,
              current: current.isOptional,
            },
          },
        });
      }
    });

    oldStage.assignedApprovers.forEach(previous => {
      if (!currentApprovers.has(previous.id)) {
        approversDeleted.push(previous);
      }
    });

    const stageChanged
      = approversAdded.length
      || approversDeleted.length
      || approversUpdated.length
      || currentStage.requiredCount !== oldStage.requiredCount
      || currentStage.name !== oldStage.name
      || currentStage.optionalApproverRejectionRejectsStage !== oldStage.optionalApproverRejectionRejectsStage;

    if (stageChanged) {
      updatedStages.push({
        stageNumber: currentStage.stageNumber,
        requiredCountChanged:
          currentStage.requiredCount !== oldStage.requiredCount
            ? {
              old: oldStage.requiredCount,
              current: currentStage.requiredCount,
            }
            : undefined,
        optionalApproverRejectionRejectsStage:
            currentStage.optionalApproverRejectionRejectsStage !== oldStage.optionalApproverRejectionRejectsStage
              ? {
                old: oldStage.optionalApproverRejectionRejectsStage,
                current: currentStage.optionalApproverRejectionRejectsStage,
              }
              : undefined,
        name: currentStage.name !== oldStage.name
          ? {
            old: oldStage.name,
            current: currentStage.name,
          }
          : undefined,
        approversAdded,
        approversDeleted,
        approversUpdated,
      });
    }
  });

  const hasChanges
    = addedStages.length > 0 || deletedStages.length > 0 || updatedStages.length > 0;

  return {
    hasChanges,
    addedStages,
    deletedStages,
    updatedStages,
  };
};
